import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fdt } from "@/lib/format";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading } from "@/components/pawkt/Empty";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Award } from "lucide-react";
import { toast } from "sonner";

export default function Loyalty() {
  const qc = useQueryClient();
  const [cfg, setCfg] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["loyalty-config"], queryFn: async () => (await api.get("/loyalty/config")).data });
  const { data: ledger } = useQuery({ queryKey: ["loyalty-ledger"], queryFn: async () => (await api.get("/loyalty/ledger")).data });
  useEffect(() => { if (data) setCfg(data); }, [data]);
  const save = useMutation({ mutationFn: (b) => api.put("/loyalty/config", b), onSuccess: () => { qc.invalidateQueries({ queryKey: ["loyalty-config"] }); toast.success("Saved"); }, onError: (e) => toast.error(apiErr(e)) });

  if (isLoading || !cfg) return <Loading />;
  const set = (k, v) => setCfg({ ...cfg, [k]: v });

  return (
    <div>
      <PageHeader title="Loyalty" subtitle="Configurable points with an immutable ledger" />
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2 mb-4"><Award className="h-5 w-5 text-amber-500" /><h3 className="font-display text-lg font-semibold">Earning rules</h3></div>
          <div className="space-y-3">
            <div><Label className="text-xs text-slate-500 mb-1 block">Earn mode</Label>
              <Select value={cfg.earn_mode} onValueChange={(v) => set("earn_mode", v)}><SelectTrigger className="bg-white" data-testid="loyalty-mode"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="per_amount">Per amount spent</SelectItem><SelectItem value="per_booking">Per completed booking</SelectItem></SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-500 mb-1 block">Points per ₹unit</Label><Input type="number" value={cfg.points_per_amount} onChange={(e) => set("points_per_amount", Number(e.target.value))} className="bg-white" data-testid="loyalty-ppa" /></div>
              <div><Label className="text-xs text-slate-500 mb-1 block">₹ unit</Label><Input type="number" value={cfg.amount_unit} onChange={(e) => set("amount_unit", Number(e.target.value))} className="bg-white" /></div>
              <div><Label className="text-xs text-slate-500 mb-1 block">Points per booking</Label><Input type="number" value={cfg.points_per_booking} onChange={(e) => set("points_per_booking", Number(e.target.value))} className="bg-white" /></div>
              <div><Label className="text-xs text-slate-500 mb-1 block">₹ per point (redeem)</Label><Input type="number" value={cfg.redeem_value} onChange={(e) => set("redeem_value", Number(e.target.value))} className="bg-white" /></div>
            </div>
            <Button onClick={() => save.mutate(cfg)} data-testid="loyalty-save" className="bg-slate-900 text-white">Save rules</Button>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="font-display text-lg font-semibold mb-3">Recent ledger</h3>
          <div className="divide-y divide-slate-100 max-h-96 overflow-auto">
            {(ledger?.items || []).length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No transactions yet</p>}
            {(ledger?.items || []).map((l) => (
              <div key={l.id} className="flex items-center gap-3 py-2 text-sm">
                <span className={`font-semibold ${l.points >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{l.points >= 0 ? "+" : ""}{l.points}</span>
                <span className="flex-1 text-slate-600 capitalize">{l.type} · {l.source}</span>
                <span className="text-xs text-slate-400">{fdt(l.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
